import { useState, useEffect, useCallback, useRef } from "react";
import { usePageHeader } from "@/contexts/page-header-context";
import { Card, CardContent } from "@/components/_components/card";
import { Button } from "@/components/_components/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/_components/badge";
import { Skeleton } from "@/components/_components/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/_components/dialog";
import { Eye, Play, Search, RotateCcw, AlertCircle, Loader2, Briefcase, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight, Pencil, MessageSquare, History } from "lucide-react";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { useChatHistory } from "@/hooks/useChatHistory";
import { getErrorMessage } from "@/lib/error-utils";
import * as api from "@/lib/api/client";
import { JsonViewerDialog } from "@/components/json-viewer-dialog";
import { StartJobDialog } from "@/components/start-job-dialog";
import ChatHistoryDialog from "@/components/chat-history-dialog";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { PAGINATION } from "@/constants/constants";
import { env, envUtils } from "@/lib/env";
import { useAuthorities } from "@/hooks/useAuthorities";

interface TpmoFilter {
  agentType: string;
  externalCd?: string;
  countryCode?: string;
  statuses?: string[];
  startDt?: string;
  endDt?: string;
}

interface TpmoData {
  id: number;
  recipientId: number;
  externalCd: string;
  title: string;
  recipientCd: string;
  recipientName: string;
  recipientPhoneNo: string;
  city: string;
  appointmentDateTime: string;
  finishDateTime: string;
  countryCode: string;
  account: string;
  program: string;
  jobSite: string;
  deviceMake: string;
  deviceModel: string;
  jobIngestionStatus: string;
  status: string;
  preVisit?: any; // JSON object for pre-visit data
}

const STATUSES = [
  "CREATED",
  "OUTREACHED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED"
];

const TpmoPage: React.FC = () => {
  return (
    <PageAccessGuard authority="CAMPAIGN_TPMO_VIEW">
      <TpmoContent />
    </PageAccessGuard>
  );
};

const TpmoContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  const navigate = useNavigate();
  const { hasAuthority } = useAuthorities();

  // Check if testing features should be shown (not in production and ADMIN_TESTING_UPDATE is enabled)
  const showTestingFeatures = !envUtils.isProduction() && hasAuthority('ADMIN_TESTING_UPDATE');

  const [filters, setFilters] = useState({
    jobNo: "",
    country: "all",
    status: "all",
    appointmentStartDt: "",
    appointmentEndDt: "",
  });
  const [pageSize, setPageSize] = useState(env.DEFAULT_PAGE_SIZE);
  const currentFilterRef = useRef<TpmoFilter>({
    agentType: "TPMO",
  });

  // Fetch countries from API
  const { data: countriesData } = useQuery({
    queryKey: ["countries"],
    queryFn: async () => {
      const response = await api.getCountriesList();
      const data = response.data || [];
      // Return full country objects with both code and name
      return data.map((country: any) => ({
        countryCode: country.countryCode,
        countryName: country.countryName,
      }));
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const countries = countriesData || [];

  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  // Start job dialog state
  const [startJobDialogOpen, setStartJobDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJobPhone, setSelectedJobPhone] = useState<string>("");
  const [selectedJobAppointmentDateTime, setSelectedJobAppointmentDateTime] = useState<string>("");
  const [selectedJobCountryCode, setSelectedJobCountryCode] = useState<string>("");

  // Pre-visit dialog state
  const [preVisitDialogOpen, setPreVisitDialogOpen] = useState(false);
  const [selectedPreVisitData, setSelectedPreVisitData] = useState<any>(null);

  // Update phone dialog state
  const [updatePhoneDialogOpen, setUpdatePhoneDialogOpen] = useState(false);
  const [updatePhoneId, setUpdatePhoneId] = useState<number | null>(null);
  const [updatePhoneValue, setUpdatePhoneValue] = useState<string>("");
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);

  // Chat History dialog state
  const [isChatHistoryDialogOpen, setIsChatHistoryDialogOpen] = useState(false);
  const [chatHistoryData, setChatHistoryData] = useState<any[]>([]);
  const [currentRecipientForChat, setCurrentRecipientForChat] = useState<any>(null);
  // Chat history hook
  const { fetchChatHistory, isLoadingChatHistory } = useChatHistory({
    onSuccess: (chatHistoryData, recipient) => {
      setChatHistoryData(chatHistoryData);
      setCurrentRecipientForChat(recipient);
      setIsChatHistoryDialogOpen(true);
    }
  });
  const [selectedRowForChat, setSelectedRowForChat] = useState<TpmoData | null>(null);

  // Global keyboard shortcut for CSV download in chat history dialog
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+D or Cmd+Shift+D to download CSV when chat history dialog is open
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D' && isChatHistoryDialogOpen && chatHistoryData.length > 0) {
        e.preventDefault();
        // The ChatHistoryDialog component will handle the download
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isChatHistoryDialogOpen, chatHistoryData.length]);

  const paginatedQuery = usePaginatedQuery<TpmoFilter, any[]>(
    (filter, options) => {
      return api.getTpmoTransMasters(filter, options);
    },
    { pageSize }
  );

  const {
    data: tpmoData,
    isPending: isLoading,
    error,
    executeQuery,
    currentPage,
    totalCount: total,
  } = paginatedQuery;

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = useCallback(() => {
    // Auto-fill missing date: if start date is provided without end date, set end date to start date and vice versa
    let startDt = filters.appointmentStartDt;
    let endDt = filters.appointmentEndDt;

    if (startDt && !endDt) {
      endDt = startDt;
      setFilters(prev => ({ ...prev, appointmentEndDt: startDt }));
    } else if (!startDt && endDt) {
      startDt = endDt;
      setFilters(prev => ({ ...prev, appointmentStartDt: endDt }));
    }

    // Validate that end date is >= start date
    if (startDt && endDt) {
      const startDate = new Date(startDt);
      const endDate = new Date(endDt);
      if (endDate < startDate) {
        toast.error("End date must be greater than or equal to start date");
        return;
      }
    }

    const filter: TpmoFilter = {
      agentType: "TPMO",
      ...(filters.jobNo && { externalCd: filters.jobNo }),
      // Send countryCode when a country is selected (not "all")
      ...(filters.country && filters.country !== "all" && { countryCode: filters.country }),
      // Send empty array for statuses when "all" is selected, otherwise send the selected status
      statuses: filters.status === "all" ? [] : [filters.status],
      // Convert dates to OffsetDateTime format (ISO 8601 with timezone)
      ...(startDt && {
        startDt: new Date(startDt).toISOString()
      }),
      ...(endDt && {
        endDt: new Date(endDt + 'T23:59:59').toISOString()
      }),
    };
    currentFilterRef.current = filter;
    executeQuery(filter, 1);
  }, [filters, executeQuery]);

  const handleReset = useCallback(() => {
    setFilters({
      jobNo: "",
      country: "all",
      status: "all",
      appointmentStartDt: "",
      appointmentEndDt: "",
    });
    const filter: TpmoFilter = { agentType: "TPMO" };
    currentFilterRef.current = filter;
    executeQuery(filter, 1);
  }, [executeQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission/page refresh
      handleSearch();
    }
  };

  const handleView = async (id: number) => {
    try {
      const response = await api.getTpmoTransMasterById(id);

      // Use addDetails if available, otherwise use the full data
      let detailsData = response.data?.addDetails || response.data;

      // If addDetails is a string, parse it
      if (typeof detailsData === 'string') {
        try {
          detailsData = JSON.parse(detailsData);
        } catch (e) {
          console.error("Failed to parse addDetails:", e);
        }
      }

      setSelectedRecord(detailsData);
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      console.error("Error fetching record:", error);
    }
  };

  const handleStartJob = (id: number, phoneNo: string, appointmentDateTime: string, countryCode?: string) => {
    setSelectedJobId(id);
    setSelectedJobPhone(phoneNo);
    setSelectedJobAppointmentDateTime(appointmentDateTime);
    setSelectedJobCountryCode(countryCode || "");
    setStartJobDialogOpen(true);
  };

  const handleViewPreVisit = (preVisitData: any) => {
    let parsedData = preVisitData;

    // If preVisitData is a string, try to parse it as JSON
    if (typeof preVisitData === 'string') {
      try {
        parsedData = JSON.parse(preVisitData);
      } catch (error) {
        console.error("Failed to parse preVisit JSON:", error);
        // If parsing fails, show the raw string
        parsedData = { rawData: preVisitData };
      }
    }

    setSelectedPreVisitData(parsedData);
    setPreVisitDialogOpen(true);
  };

  const handleStartJobSubmit = async (data: { appointmentTime: string; phoneNo: string; timezone: string; countryCode: string; finishTime?: string }) => {
    if (!selectedJobId) return;

    try {
      await api.startTpmoJob(selectedJobId, data);
      toast.success("Job started successfully");
      // Refresh the data
      executeQuery(currentFilterRef.current, currentPage);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      console.error("Error starting job:", error);
      throw error;
    }
  };

  const handleUpdatePhone = (id: number, currentPhone: string) => {
    setUpdatePhoneId(id);
    setUpdatePhoneValue(currentPhone);
    setUpdatePhoneDialogOpen(true);
  };

  const handleUpdatePhoneSubmit = async () => {
    if (!updatePhoneId || !updatePhoneValue.trim()) {
      toast.error("Phone number is required");
      return;
    }

    // Validate phone number format: must start with + and contain only numbers after that
    const phoneRegex = /^\+\d+$/;
    if (!phoneRegex.test(updatePhoneValue)) {
      toast.error("Phone number must start with + and contain only numbers");
      return;
    }

    setIsUpdatingPhone(true);
    try {
      await api.updateTpmoPhoneNo(updatePhoneId, updatePhoneValue);
      toast.success("Phone number updated successfully");
      setUpdatePhoneDialogOpen(false);
      // Refresh the data
      executeQuery(currentFilterRef.current, currentPage);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      console.error("Error updating phone number:", error);
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  const handleWebchat = (transId: number) => {
    navigate(`${ROUTES.WEBCHAT}?transId=${transId}&agentType=TPMO`);
  };

  const initialLoadRef = useRef(false);

  // Simple initial load on mount
  useEffect(() => {
    const performInitialLoad = () => {
      if (initialLoadRef.current) return;
      initialLoadRef.current = true;

      const filter = { agentType: "TPMO" };
      currentFilterRef.current = filter;
      executeQuery(filter, 1);
    };

    // Small delay to ensure everything is ready
    const timer = setTimeout(performInitialLoad, 100);
    return () => clearTimeout(timer);
  }, []); // Empty dependency array

  // Auto-refresh every 2 minutes - simulates clicking Search button
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      if (initialLoadRef.current) {
        handleSearch();
      }
    }, 2 * 60 * 1000); // 2 minutes in milliseconds

    return () => clearInterval(autoRefreshInterval);
  }, [handleSearch]);

  useEffect(() => {
    setHeaderData({
      stats: tpmoData ? {
        count: total,
        label: 'work order',
        icon: <Briefcase className="h-4 w-4" />
      } : undefined,
    });
    return () => {
      setHeaderData(null);
    };
  }, [tpmoData, total, setHeaderData]);

  if (isLoading && !tpmoData) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border border-border/50 bg-card backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border border-destructive/30 bg-card backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Error loading data</h3>
              <Button onClick={() => window.location.reload()} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const tableData: TpmoData[] = tpmoData?.map((row, index) => {
    // Handle preVisit data - it might be a JSON string that needs parsing
    let preVisitData = row.preVisit || row.pre_visit || null;

    return {
      id: row.id || index + 1,
      recipientId: row.recipientId || row.recipient_id || row.id || index + 1,
      externalCd: row.externalCd || row.externalId || "N/A",
      title: row.title || "N/A",
      recipientCd: row.recipientCd || row.recipient_cd || "N/A",
      recipientName: row.recipientName || row.workerName || row.worker_name || "N/A",
      recipientPhoneNo: row.recipientPhoneNo || row.workerPhone || row.worker_phone || row.phoneNo || "N/A",
      city: row.location || "N/A",
      appointmentDateTime: row.appointmentDateTime || row.appointment_date_time || "N/A",
      finishDateTime: row.finishDateTime || "N/A",
      countryCode: row.countryCode || "N/A",
      account: row.account || "N/A",
      program: row.program || "N/A",
      jobSite: row.jobSite || "N/A",
      deviceMake: row.deviceMake || "N/A",
      deviceModel: row.deviceModel || "N/A",
      jobIngestionStatus: row.jobIngestionStatus || "N/A",
      status: row.status || "PENDING",
      preVisit: preVisitData,
    };
  }) || [];

  // Format date time to local format
  const formatDateTime = (dateTimeStr: string) => {
    if (!dateTimeStr || dateTimeStr === "N/A") return "N/A";
    try {
      const date = new Date(dateTimeStr);
      // Format: Jan 27, 2026, 6:28 PM
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return dateTimeStr;
    }
  };

  return (
    <div className="bg-background">
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {/* Filters Section */}
          <div className="bg-card border border-border/50 rounded-lg">
            <div className="px-4 pt-2 pb-1 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Find TPMO work orders
                  </p>
                </div>
              </div>
            </div>

            <div className="px-3 pb-3">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="jobNo" className="text-xs font-medium">
                    Job No
                  </Label>
                  <Input
                    id="jobNo"
                    placeholder="Enter job number"
                    value={filters.jobNo}
                    onChange={(e) => handleFilterChange("jobNo", e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="country" className="text-xs font-medium">
                    Country
                  </Label>
                  <Select value={filters.country} onValueChange={(value) => handleFilterChange("country", value)}>
                    <SelectTrigger id="country" className="h-9">
                      <SelectValue placeholder="Select Country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.map((country) => (
                        <SelectItem key={country.countryCode} value={country.countryCode}>
                          {country.countryCode} - {country.countryName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="appointmentStartDt" className="text-xs font-medium">
                    From Appointment Start Date
                  </Label>
                  <Input
                    id="appointmentStartDt"
                    type="date"
                    value={filters.appointmentStartDt}
                    onChange={(e) => handleFilterChange("appointmentStartDt", e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="appointmentEndDt" className="text-xs font-medium">
                    To Appointment Start Date
                  </Label>
                  <Input
                    id="appointmentEndDt"
                    type="date"
                    value={filters.appointmentEndDt}
                    onChange={(e) => handleFilterChange("appointmentEndDt", e.target.value)}
                    onKeyDown={handleKeyDown}
                    min={filters.appointmentStartDt || undefined}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="status" className="text-xs font-medium">
                    Status
                  </Label>
                  <Select value={filters.status} onValueChange={(value) => handleFilterChange("status", value)}>
                    <SelectTrigger id="status" className="h-9">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button onClick={handleSearch} disabled={isLoading} className="h-9">
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-1.5" />
                      Search
                    </>
                  )}
                </Button>
                <Button onClick={handleReset} disabled={isLoading} variant="outline" className="h-9">
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Results Table */}
          <Card className="border border-border/50 bg-card backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Briefcase className="h-5 w-5 text-primary" />
                </div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2 className="text-xl font-bold text-foreground">TPMO Work Orders</h2>
                  <Badge variant="secondary" className="text-xs font-medium">
                    {total} {total === 1 ? 'record' : 'records'}
                  </Badge>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-border/50">
                <Table>
                  <TableHeader className="bg-muted/95">
                    <TableRow>
                      <TableHead>Job No</TableHead>
                      <TableHead>Job Title</TableHead>
                      <TableHead>Worker Name</TableHead>
                      <TableHead>Worker Phone</TableHead>
                      <TableHead>Work Location</TableHead>
                      <TableHead>Appointment Date/Time</TableHead>
                      <TableHead>Finish Date/Time</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Job Site</TableHead>
                      <TableHead>Device Make</TableHead>
                      <TableHead>Device Model</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ingestion Status</TableHead>
                      <TableHead>Process Details</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading && !tpmoData ? (
                      Array.from({ length: 5 }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          {Array.from({ length: 12 }).map((_, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : tableData.length > 0 ? (
                      tableData.map((row) => (
                        <TableRow key={row.id} className="hover:bg-muted/50">
                          <TableCell className="font-medium">
                            <button
                              onClick={() => handleView(row.id)}
                              className="text-primary hover:text-primary/80 hover:underline cursor-pointer transition-colors"
                              title="View Details"
                            >
                              {row.externalCd}
                            </button>
                          </TableCell>
                          <TableCell>{row.title}</TableCell>
                          <TableCell>{row.recipientName}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{row.recipientPhoneNo}</span>
                              {showTestingFeatures && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => handleUpdatePhone(row.id, row.recipientPhoneNo)}
                                  title="Edit Phone Number"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{row.city}</TableCell>
                          <TableCell>{formatDateTime(row.appointmentDateTime)}</TableCell>
                          <TableCell>{formatDateTime(row.finishDateTime)}</TableCell>
                          <TableCell>{row.countryCode}</TableCell>
                          <TableCell>{row.account}</TableCell>
                          <TableCell>{row.program}</TableCell>
                          <TableCell>{row.jobSite}</TableCell>
                          <TableCell>{row.deviceMake}</TableCell>
                          <TableCell>{row.deviceModel}</TableCell>
                          <TableCell>
                            <Badge variant={row.status === "COMPLETED" ? "default" : "secondary"}>
                              {row.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{row.jobIngestionStatus}</TableCell>
                          <TableCell>
                            {row.preVisit ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-3 text-primary hover:text-primary/80"
                                onClick={() => handleViewPreVisit(row.preVisit)}
                                title="View Pre-Visit Communication Data"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-sm italic">No data</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {showTestingFeatures && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleStartJob(row.id, row.recipientPhoneNo, row.appointmentDateTime, row.countryCode)}
                                  title="Start Job"
                                >
                                  <Play className="h-4 w-4" />
                                </Button>
                              )}
                              {row.status !== "CLOSED" && (
                                <>
                                  {showTestingFeatures && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => handleWebchat(row.id)}
                                      title="Web Chat"
                                    >
                                      <MessageSquare className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
                                    onClick={() => {
                                      setSelectedRowForChat(row);
                                      fetchChatHistory(row.recipientId, undefined, 'TPMO');
                                    }}
                                    title="Chat History - View conversation history between the recipient and AI assistant"
                                    disabled={isLoadingChatHistory}
                                  >
                                    <History className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={12} className="h-24 text-center">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <AlertCircle className="h-8 w-8 text-muted-foreground/50" />
                            <span>No results found</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 mt-4">
                <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                  {total} {total === 1 ? 'record' : 'records'}
                </div>
                <div className="flex w-full items-center gap-8 lg:w-fit">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page</span>
                    <Select
                      value={pageSize.toString()}
                      onValueChange={(value) => {
                        const newSize = Number(value);
                        setPageSize(newSize);
                        executeQuery(currentFilterRef.current, 1, newSize);
                      }}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="h-8 w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                          <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      Page {currentPage} of {Math.ceil(total / pageSize) || 1}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => executeQuery(currentFilterRef.current, 1, pageSize)}
                        disabled={currentPage === 1 || isLoading}
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => executeQuery(currentFilterRef.current, currentPage - 1, pageSize)}
                        disabled={currentPage === 1 || isLoading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => executeQuery(currentFilterRef.current, currentPage + 1, pageSize)}
                        disabled={currentPage >= Math.ceil(total / pageSize) || isLoading}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => executeQuery(currentFilterRef.current, Math.ceil(total / pageSize), pageSize)}
                        disabled={currentPage >= Math.ceil(total / pageSize) || isLoading}
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <JsonViewerDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        title="Work Order Details"
        data={selectedRecord}
      />
      <StartJobDialog
        open={startJobDialogOpen}
        onOpenChange={setStartJobDialogOpen}
        onSubmit={handleStartJobSubmit}
        initialPhoneNo={selectedJobPhone}
        initialAppointmentDateTime={selectedJobAppointmentDateTime}
        initialCountryCode={selectedJobCountryCode}
      />

      {/* Update Phone Dialog */}
      <Dialog open={updatePhoneDialogOpen} onOpenChange={setUpdatePhoneDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Phone Number</DialogTitle>
            <DialogDescription>
              Update the worker's phone number for this work order.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="phoneNo">
                Phone Number
              </Label>
              <Input
                id="phoneNo"
                value={updatePhoneValue}
                onChange={(e) => {
                  setUpdatePhoneValue(e.target.value);
                }}
                placeholder="+1234567890"
                disabled={isUpdatingPhone}
              />
              <p className="text-xs text-muted-foreground">
                Phone number must start with + and contain only numbers
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUpdatePhoneDialogOpen(false)}
              disabled={isUpdatingPhone}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePhoneSubmit}
              disabled={isUpdatingPhone || !updatePhoneValue.trim()}
            >
              {isUpdatingPhone ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-Visit Data Dialog */}
      <JsonViewerDialog
        open={preVisitDialogOpen}
        onOpenChange={setPreVisitDialogOpen}
        title="Pre-Visit Communication Data"
        data={selectedPreVisitData}
      />

      {/* Chat History Dialog */}
      <ChatHistoryDialog
        isOpen={isChatHistoryDialogOpen}
        onClose={() => setIsChatHistoryDialogOpen(false)}
        isLoading={isLoadingChatHistory}
        chatHistoryData={chatHistoryData}
        recipientData={selectedRowForChat ? {
          fullName: selectedRowForChat.recipientName,
          emailId: undefined,
          mobilePhoneNo: selectedRowForChat.recipientPhoneNo,
        } : undefined}
        transId={currentRecipientForChat?.transId?.toString()}
        onDownloadCSV={undefined}
      />
    </div>
  );
}


export default TpmoPage;
